import { useCheckFlag } from '@speeki/feature-flags';
import { StorageManager } from '@speeki/storage-manager';

import {
    UserSessionDocument,
    UserSessionQuery,
    UserSessionQueryVariables,
} from '@graphql/generated/graphql';

import { AuthService } from '@services/AuthService/AuthService';

import { useLazyQuery } from '@apollo/client';
import { QueryHookOptions } from '@apollo/client/react/types/types';
import { authVar } from '@boot/reactiveVariables';

interface UseUserSession
    extends QueryHookOptions<UserSessionQuery, UserSessionQueryVariables> {}

/**
 * Use to get user data after the session is initialized/changed or to check if the user is logged in.
 * **Do not use for getting data of the user in other parts of the app (do another query).**
 * We want always to have updated data so do not change the **network-only** policy.
 */
export const useUserSession = (props?: UseUserSession) => {
    const isEngagementFeatureEnabled = useCheckFlag(
        'client_admin-stakeholder_engagement-20250312',
    );

    return useLazyQuery(UserSessionDocument, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ user }) => {
            authVar({
                isGuest: user.type === 'GUEST',
                isLoggedIn: true,
                isMfa: false,
            });
            StorageManager.removeValue('metadata');
        },
        onError: () => void AuthService.logout(true),
        variables: {
            isEngagementFeatureEnabled,
        },
        ...props,
    });
};
